import { useState, useEffect, useCallback, useMemo } from 'react'
import { Row, Col, Nav, Tab } from 'react-bootstrap'
import { Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  editAbsenceSalaryMappingBatch,
  createExternalMapping
} from '../../api/endpoints/calendar-data-rest-controller/calendar-data-rest-controller'
import {
  getCalendarSettings,
  setCalendarSettings,
  useGetCalendarEventSettings,
  setCalendarEventSettings,
  getGetCalendarEventSettingsQueryKey
} from '../../api/endpoints/organization-data-rest-controller/organization-data-rest-controller'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { activeIntegrationsSelector } from '../../reducers'
import { EventSettings } from './event-settings/EventSettings.jsx'
import { WorkingTimeSettings } from './working-time-settings/WorkingTime.jsx'
import { Button } from '../../common/Button.tsx'
import { LoadingOverlay } from '../../common/loaders/LoadingOverlay.jsx'
import { 
  buildOrgEventSettingsMap, 
  mergeMappingsFrom,
  mergeNoIntegrationMappingsFrom
} from './utils.jsx'
import { useGetAllAbsenceSalaryMappings } from '../../api/endpoints/index.ts'
import { useGetEventTypesOptions } from '../../organizationview/integrations/modals/SettingsProcountor/hooks/useGetEventTypesOptions.js'

export const CalendarSettings = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState('event-settings')
  const [settings, setSettings] = useState(null)
  const [originalSettings, setOriginalSettings] = useState(null)
  const [mappings, setMappings] = useState([])
  const [originalMappings, setOriginalMappings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const activeIntegrations = useSelector(activeIntegrationsSelector)
  
  const integrationMode = useMemo(() => {
    if (activeIntegrations?.tokenProcountor) return 'PROCOUNTOR'
    if (activeIntegrations?.settingsEsalary) return 'ESALARY'
    return 'NOINTEGRATION'
  }, [activeIntegrations])
  
  const { data: mappingsData, refetch } = useGetAllAbsenceSalaryMappings()

  const filteredData = useMemo(
    () => mappingsData?.filter((item) => item.payrollSystemType === integrationMode) || [],
    [mappingsData, integrationMode]
  )

  // For NOINTEGRATION: backend defaults to payrollSystemType=ESALARY for all orgs.
  // Treat those ESALARY records as the base data for NOINTEGRATION users.
  // If a true NOINTEGRATION record ever exists, prefer it over the ESALARY default.
  const noIntegrationMappingsLookup = useMemo(() => {
    if (integrationMode !== 'NOINTEGRATION') return {}
    return (mappingsData || []).reduce((acc, m) => {
      const existing = acc[m.absenceType]
      const incomingIsNoIntegration = m.payrollSystemType === 'NOINTEGRATION'
      const existingIsNoIntegration = existing?.payrollSystemType === 'NOINTEGRATION'
      if (!existing || (incomingIsNoIntegration && !existingIsNoIntegration)) {
        acc[m.absenceType] = { ...m, payrollSystemType: 'NOINTEGRATION' }
      }
      return acc
    }, {})
  }, [mappingsData, integrationMode])

  const eventTypesOptions = useGetEventTypesOptions()

  const { data: calendarEventSettings } = useGetCalendarEventSettings({
    query: {
      enabled: Boolean(settings),
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  })

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true)
        setError(null)

        const [settingsRes] = await Promise.all([
          getCalendarSettings()
        ])

        setSettings(settingsRes)
        setOriginalSettings(settingsRes)
      } catch (err) {
        console.error('Error fetching calendar settings:', err)
        setError(t('ErrorLoadingSettings', 'Virhe asetuksien lataamisessa'))
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [integrationMode, t])

  useEffect(() => {
    if (loading) return
    if (!settings) return
    if (!eventTypesOptions.length) {
      setMappings([])
      setOriginalMappings([])
      return
    }
    const orgEventSettingsMap = buildOrgEventSettingsMap(calendarEventSettings)

    let merged
    if (integrationMode === 'NOINTEGRATION') {
      merged = mergeNoIntegrationMappingsFrom({
        eventTypesOptions,
        mappingsLookup: noIntegrationMappingsLookup,
        orgEventSettingsMap
      })
    } else {
      if (!filteredData.length) {
        setMappings([])
        setOriginalMappings([])
        return
      }
      merged = mergeMappingsFrom({
        filteredMappings: filteredData,
        eventTypesOptions,
        orgEventSettingsMap,
        integrationMode
      })
    }

    setMappings(merged)
    setOriginalMappings(merged)
  }, [calendarEventSettings, eventTypesOptions, filteredData, integrationMode, loading, noIntegrationMappingsLookup, settings])

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleMappingChange = (updatedMappings) => {
    setMappings(updatedMappings)
  }

  /** Constructs the API payload for saving calendar settings */
  const buildCalendarSettingsPayloadFrom = useCallback((s) => ({
    emailNotificationsEnabled: s?.emailNotificationsEnabled,
    sharedCalendarVisibleOnProfile: s?.sharedCalendarVisibleOnProfile,
    sharedCalendarShowOnlyHolidays: s?.sharedCalendarShowOnlyHolidays,
    saturdaysCalculatedAsHoliday: s?.saturdaysCalculatedAsHoliday ?? true,
    leaveBookingBalanceLimitOnly: s?.leaveBookingBalanceLimitOnly,
    calculationYearOption: s?.calculationYearOption,
    selfCertificationEnabled: s?.selfCertificationEnabled,
    icdCodeRequired: s?.icdCodeRequired
  }), [])

  const buildCalendarSettingsPayload = useCallback(() => (
    buildCalendarSettingsPayloadFrom(settings)
  ), [buildCalendarSettingsPayloadFrom, settings])

  /** Returns event settings where useStatus differs from original (for org-level visibility) */
  const getChangedEventSettings = useCallback(() => {
    const defaultOriginalUseStatus = integrationMode === 'PROCOUNTOR' ? false : true

    return mappings
      .filter((m) => {
        const original = originalMappings.find((om) => om.absenceType === m.absenceType)
        const originalUseStatus = original?.status ?? defaultOriginalUseStatus
        return m.status !== originalUseStatus
      })
      .map((m) => ({
        eventCode: m.absenceType,
        useStatus: m.status
      }))
  }, [integrationMode, mappings, originalMappings])

  /** Returns mappings where salaryTypeCode or useStatus changed (for NOINTEGRATION backend sync) */
  const getChangedExternalMappings = useCallback(() => {
    return mappings.filter((m) => {
      const original = originalMappings.find((om) => om.absenceType === m.absenceType)

      const currentSalary = (m.salaryTypeCode || '').trim()
      const originalSalary = (original?.salaryTypeCode || '').trim()

      const salaryChanged = currentSalary !== originalSalary
      const useStatusChanged = (m.status ?? true) !== (original?.status ?? true)

      return salaryChanged || useStatusChanged
    })
  }, [mappings, originalMappings])

  /** Persists changed absence-salary mappings to backend (batch update + individual creates) */
  const saveSalaryTypeMappings = useCallback(async () => {
    const changedMappings = getChangedExternalMappings()
    if (changedMappings.length === 0) return

    const toUpdate = changedMappings.filter((m) => m.id && !m.isNew)
    const toCreate = changedMappings.filter((m) => (!m.id || m.isNew) && (m.salaryTypeCode || '').trim())

    const promises = []

    if (toUpdate.length > 0) {
      const updatePayload = toUpdate.map((m) => {
        const salaryTypeCode = (m.salaryTypeCode || '').trim() || null
        return {
          id: m.id,
          absenceType: m.absenceType,
          salaryTypeCode,
          payrollSystemType: integrationMode,
          active: Boolean(m.status) && Boolean(salaryTypeCode),
          formOfSalary: m.formOfSalary ?? null,
          paymentGroup: m.paymentGroup ?? ''
        }
      })
      promises.push(editAbsenceSalaryMappingBatch(updatePayload))
    }

    toCreate.forEach((m) => {
      const salaryTypeCode = (m.salaryTypeCode || '').trim() || null
      const dto = {
        absenceType: m.absenceType,
        salaryTypeCode,
        payrollSystemType: integrationMode,
        active: Boolean(m.status) && Boolean(salaryTypeCode),
        formOfSalary: m.formOfSalary ?? null,
        paymentGroup: m.paymentGroup ?? ''
      }
      promises.push(createExternalMapping(dto))
    })

    await Promise.all(promises)
  }, [getChangedExternalMappings, integrationMode])

  /** Re-fetches all data from server and resets local + original state to sync UI */
  const refreshData = useCallback(async () => {
    const [settingsRes] = await Promise.all([
      getCalendarSettings(),
    ])

    setSettings(settingsRes)
    setOriginalSettings(settingsRes)
    await refetch()
    await queryClient.invalidateQueries({ queryKey: getGetCalendarEventSettingsQueryKey() })
  }, [queryClient, refetch])

  const currentSettingsPayload = useMemo(
    () => buildCalendarSettingsPayloadFrom(settings),
    [buildCalendarSettingsPayloadFrom, settings]
  )
  const originalSettingsPayload = useMemo(
    () => buildCalendarSettingsPayloadFrom(originalSettings),
    [buildCalendarSettingsPayloadFrom, originalSettings]
  )

  const hasSettingsChanged = JSON.stringify(currentSettingsPayload) !== JSON.stringify(originalSettingsPayload)

  /** Strips mappings to only compare-relevant fields for change detection */
  const normalizeMappingsForCompare = useCallback((list) => (
    (list || []).map((m) => ({
      absenceType: m.absenceType,
      status: Boolean(m.status),
      salaryTypeCode: (m.salaryTypeCode || '').trim()
    }))
  ), [])

  const hasMappingsChanged =
    JSON.stringify(normalizeMappingsForCompare(mappings)) !==
    JSON.stringify(normalizeMappingsForCompare(originalMappings))

  const hasChanges = hasSettingsChanged || hasMappingsChanged
  const hasValidationErrors = false

  /** Orchestrates saving all changed settings/mappings to backend in parallel */
  const handleSaveSettings = async () => {
    if (isSaving || loading || !hasChanges) return

    try {
      setIsSaving(true)
      setError(null)

      const promises = []
      promises.push(setCalendarSettings(buildCalendarSettingsPayload()))

      const changedEventSettings = getChangedEventSettings()
      if (changedEventSettings.length > 0) {
        promises.push(setCalendarEventSettings(changedEventSettings))
      }

      if (integrationMode === 'NOINTEGRATION' || integrationMode === 'ESALARY') {
        promises.push(saveSalaryTypeMappings())
      }

      await Promise.all(promises)
      await refreshData()
    } catch (err) {
      console.error('Error saving calendar settings:', err)
      setError(t('ErrorSavingSettings', 'Virhe asetuksien tallentamisessa'))
    } finally {
      setIsSaving(false)
    }
  }

  if (error && !settings) {
    return <div className='alert alert-danger'>{error}</div>
  }

  return (
    <>
      <LoadingOverlay
        isVisible={isSaving}
        message={t('Saving', 'Tallennetaan...')}
      />

      <Row className='my-3'>
        <Col sm='4'>
          <h2 className='viewheading' style={{ fontWeight: '600' }}>
            {t('CalendarSettings', 'Työaika asetukset')}
          </h2>
        </Col>

        {error && settings && (
          <Col sm='12' className='mb-2'>
            <div className='alert alert-warning mb-0'>{error}</div>
          </Col>
        )}

        {hasChanges && (
          <Col sm='8' className='d-flex justify-content-end align-items-center'>
            <Button
              variant='primary'
              onClick={handleSaveSettings}
              disabled={isSaving || hasValidationErrors || loading}
            >
              {isSaving ? t('Saving', 'Tallennetaan...') : t('Save', 'Tallenna')}
            </Button>
          </Col>
        )}
      </Row>

      {loading ? (
        <div className='mt-3'>
          <div className='spinner-border' role='status'>
            <span className='visually-hidden'>{t('Loading', 'Ladataan...')}</span>
          </div>
        </div>
      ) : (
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className='mt-3'>
          <Nav variant='tabs' className='mb-3' style={{ borderColor: 'var(--app-background)' }}>
            <Tooltip title={t('ComingSoon', 'Tulossa pian')} placement='bottom'>
              <Nav.Item style={{ fontWeight: '600' }}>
                <Nav.Link eventKey='working-time-settings' disabled>
                  {t('WorkingTimeSettings', 'Työaika asetukset')}
                </Nav.Link>
              </Nav.Item>
            </Tooltip>

            <Nav.Item>
              <Nav.Link
                eventKey='event-settings'
                style={{ fontWeight: '600', backgroundColor: 'var(--app-background)' }}
              >
                {t('EventSettings', 'Tapahtuma asetukset')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content className='content-div'>
            <Tab.Pane eventKey='working-time-settings'>
              <WorkingTimeSettings />
            </Tab.Pane>

            <Tab.Pane eventKey='event-settings'>
              <EventSettings
                settings={settings}
                mappings={mappings}
                onSettingChange={handleSettingChange}
                onMappingChange={handleMappingChange}
                onSave={handleSaveSettings}
                isSaving={isSaving}
                hasChanges={hasChanges}
                integrationMode={integrationMode}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}
    </>
  )
}